import React, { useState } from "react";
import * as classes from "./flowStepVideoSelect.module.scss";
import classNames from "classnames";
import { useForm } from "react-hook-form";
import IconCheckmarkCircle from "@components/icons/iconCheckmarkCircle";
import { CtaVideoButton } from "@components/shared/cta";
import useUserAgent from "@hooks/useUserAgent";

const Option = ({ img, value, label, videoId, onOptionAdded, onOptionRemoved, isSelected, deactivated }) => {
  const { isMobile } = useUserAgent();
  const onClick = () => {
    if (videoId) {
      onOptionAdded(value, videoId);
      setTimeout(() => {
        // Timeout necessary to ensure modal closes before video opens
        if (document.getElementById("video-button-" + videoId) && isMobile) {
          document.getElementById("video-button-" + videoId).click();
        }
      }, 500);
      return;
    }
    if (!deactivated) {
      !isSelected ? onOptionAdded(value) : onOptionRemoved(value);
    }
  };

  const cx = classNames(classes.item, "multiselectItem", {
    [classes.selected]: isSelected,
    [classes.deactivated]: !isSelected && deactivated,
  });

  return (
    <div className={cx} onClick={onClick}>
      <div className={classes.nameAndIconCt}>
        <div className={classes.imgCtn}>
          <img src={img} alt={label + " icon"} />
        </div>
        <span className={classes.title}>{label}</span>
      </div>
      <IconCheckmarkCircle className={classNames(classes.selectedIcon, isSelected && classes.isSelected)} />
    </div>
  );
};

export default function FlowStepVideoSelect({ onContinue, options, singleSelect, minimumSelection, videoIds }) {
  const { handleSubmit } = useForm();
  const [deactivated, setDeactivated] = useState(false);
  const [selectedServices, setSelectedServices] = useState(() => new Set());
  const [videoIdSet] = useState(videoIds ? Array.from(new Set(videoIds)) : null);
  const [selectedVideo, setSelectedVideo] = useState("");
  const [selectedService, setSelectedService] = useState("");

  const onSubmit = () => {
    setTimeout(() => {
      if (minimumSelection && selectedServices.size < minimumSelection) {
        return;
      }
      setDeactivated(true);
      onContinue([...selectedServices]);
    }, 500);
  };

  const onOptionAdded = (value, videoId) => {
    if (videoId) {
      setSelectedVideo(videoId);
      setSelectedService(value);
      // Let's set the localStorage value upon selection, no need to wait for the form submission
      try {
        window.localStorage.setItem("videoId", videoId);
      } catch (error) {
        console.error("Error setting localStorage item:", error);
      }
    }
    const newSet = new Set(selectedServices);
    if (singleSelect) {
      newSet.clear();
    }
    newSet.add(value);
    setSelectedServices(newSet);
  };

  const onOptionRemoved = (value) => {
    const newSet = new Set(selectedServices);
    newSet.delete(value);
    setSelectedServices(newSet);
  };

  const optionElements = options.map((o) => {
    return (
      <Option
        img={o.img}
        value={o.value}
        label={o.label}
        videoId={o.videoId || null}
        key={"option-" + o.value}
        onOptionAdded={onOptionAdded}
        onOptionRemoved={onOptionRemoved}
        isSelected={selectedServices.has(o.value)}
        deactivated={deactivated}
      />
    );
  });

  const { isMobile } = useUserAgent();

  return (
    <div className={classNames(classes.container, "flow-step-multi-select")}>
      {optionElements}
      <form className={isMobile && classes.mobileForm} onSubmit={handleSubmit(onSubmit)}>
        {videoIdSet.map((videoId) => {
          const isSelected = videoId === selectedVideo;
          return (
            <CtaVideoButton
              id={`video-button-${videoId}`}
              key={`video-button-${videoId}-${isSelected}`}
              className={classNames(classes.btn, classes.videoModal, isSelected && classes.enabled)}
              showIcon
              label={"Watch video"}
              wistiaVideoId={videoId}
              style={"Button Primary"}
              trackedEventName={`Video Identity Selection: ${selectedService}`}
            />
          );
        })}
      </form>
    </div>
  );
}
